import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../hooks/useQueue';
import { useSettings } from '../contexts/SettingsContext';
import { RoleShell } from '../components/RoleShell';
import { CopperButton } from '../components/ui/copper-button';
import { GlassButton } from '../components/ui/glass-button';
import { StatusPill } from '../components/ui/status-pill';
import { toast } from 'sonner';
import { Stethoscope, FileText, Receipt, CheckCircle2, ChevronRight, IndianRupee, Clock, User as UserIcon } from 'lucide-react';

const DEFAULT_CHARGES = [
  { code: 'CONSULT_NEW',     label: 'New consultation',     amount: 500 },
  { code: 'CONSULT_FOLLOW',  label: 'Follow-up',            amount: 300 },
  { code: 'CONSULT_TELE',    label: 'Tele-consultation',    amount: 400 },
  { code: 'PROC_INJ',        label: 'Injection',            amount: 200 },
  { code: 'PROC_DRESSING',   label: 'Wound dressing',       amount: 250 },
  { code: 'LAB_SUGAR',       label: 'Blood sugar test',     amount: 150 },
];

export const DoctorView = () => {
  const { token, user } = useAuth();
  const { settings } = useSettings();
  const { patients, loading, updatePatient } = useQueue({ token });
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [prescription, setPrescription] = useState('');
  const [selectedCharges, setSelectedCharges] = useState({}); // code -> qty
  const [customCharge, setCustomCharge] = useState('');

  // Queue visible to the doctor: next-in-line + in-consultation + waiting
  const doctorQueue = patients.filter(p =>
    ['in-consultation', 'next-in-line', 'waiting'].includes(p.status)
  );
  const current = patients.find(p => p.id === selectedId) ||
                  patients.find(p => p.status === 'in-consultation') ||
                  doctorQueue[0];

  // Hydrate form when current patient changes
  useEffect(() => {
    if (current) {
      setNotes(current.doctor_notes || '');
      setPrescription(current.prescription || '');
      setSelectedCharges({});
      setCustomCharge('');
    }
  }, [current?.id]);

  const totalCharges =
    Object.entries(selectedCharges).reduce((sum, [code, qty]) => {
      const item = DEFAULT_CHARGES.find(c => c.code === code);
      return sum + (item ? item.amount * qty : 0);
    }, 0) + (parseFloat(customCharge) || 0);

  const toggleCharge = (code) => {
    setSelectedCharges(prev => {
      const next = { ...prev };
      if (next[code]) delete next[code];
      else next[code] = 1;
      return next;
    });
  };

  const handleStartConsultation = async () => {
    if (!current) return;
    try {
      await updatePatient(current.id, { status: 'in-consultation' });
      toast.success(`Started consultation for ${current.patient_name}`);
    } catch (e) { toast.error('Could not start consultation'); }
  };

  const handleSaveNotes = async () => {
    if (!current) return;
    try {
      await updatePatient(current.id, { doctor_notes: notes, prescription });
      toast.success('Notes saved');
    } catch (e) { toast.error('Could not save notes'); }
  };

  const handleSendToBilling = async () => {
    if (!current) return;
    if (totalCharges <= 0) {
      toast.error('Add at least one charge before sending to billing');
      return;
    }
    try {
      await updatePatient(current.id, {
        doctor_notes: notes,
        prescription,
        amount_due: totalCharges,
        status: 'payment-pending',
      });
      toast.success(`₹${totalCharges} pushed to billing for ${current.patient_name}`);
      setSelectedId(null);
    } catch (e) { toast.error('Could not send to billing'); }
  };

  const handleComplete = async () => {
    if (!current) return;
    try {
      await updatePatient(current.id, { doctor_notes: notes, prescription, status: 'completed' });
      toast.success(`${current.patient_name} marked as completed`);
      setSelectedId(null);
    } catch (e) { toast.error('Could not complete'); }
  };

  return (
    <RoleShell titlePrefix="Doctor" userName={settings.doctor_name}>
      <div className="flex" style={{ height: 'calc(100vh - 64px)' }} data-testid="doctor-view">
        {/* Queue Sidebar */}
        <div className="glass-primary overflow-y-auto" style={{ width: '340px', padding: '20px' }}>
          <div className="flex items-center gap-2 mb-4">
            <Stethoscope size={18} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
            <h2 className="font-['Sora'] text-base font-semibold" style={{ color: '#1C1917' }}>My Queue</h2>
            <span className="ml-auto px-2 py-0.5 rounded-full font-['DM_Mono'] text-[11px] font-semibold" style={{ background: 'rgba(61,90,128,0.08)', color: 'var(--color-slate)' }}>
              {doctorQueue.length}
            </span>
          </div>

          {loading && doctorQueue.length === 0 && (
            <div className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>Loading…</div>
          )}
          {!loading && doctorQueue.length === 0 && (
            <div className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>No patients in queue right now.</div>
          )}

          <div className="flex flex-col gap-2">
            {doctorQueue.map(p => {
              const isSelected = current?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className="text-left p-3 rounded-[var(--radius-md)] transition-all duration-180"
                  style={{
                    background: isSelected ? 'rgba(212, 120, 74, 0.08)' : 'rgba(255,255,255,0.5)',
                    border: isSelected ? '1px solid rgba(212, 120, 74, 0.35)' : '1px solid rgba(28,25,23,0.06)',
                    boxShadow: isSelected ? '0 6px 16px rgba(212,120,74,0.15)' : 'none'
                  }}
                  data-testid={`doctor-queue-item-${p.token_number}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-['DM_Mono'] text-sm font-medium" style={{ color: isSelected ? '#B5612E' : 'var(--color-slate)' }}>{p.token_number}</span>
                    <StatusPill status={p.status} />
                  </div>
                  <div className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>{p.patient_name}</div>
                  <div className="font-['Inter'] text-xs mt-0.5" style={{ color: '#78716C' }}>
                    {p.chief_complaint || 'No complaint'}
                  </div>
                  {p.is_emergency && (
                    <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-['Sora'] text-[10px] font-bold" style={{ background: 'rgba(185,28,28,0.10)', color: '#B91C1C' }}>
                      EMERGENCY
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Consultation Pane */}
        <div className="flex-1 overflow-y-auto" style={{ background: 'var(--color-cream)', padding: '28px 32px' }}>
          {!current && (
            <div className="glass-primary rounded-[var(--radius-2xl)] p-16 text-center">
              <Stethoscope size={42} strokeWidth={1.5} style={{ color: 'var(--color-slate)', opacity: 0.4, margin: '0 auto 16px' }} />
              <h2 className="font-['Sora'] text-xl font-bold mb-2" style={{ color: '#1C1917' }}>No patient selected</h2>
              <p className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>Pick a patient from the queue to begin</p>
            </div>
          )}

          {current && (
            <>
              {/* Patient Header */}
              <div className="glass-primary rounded-[var(--radius-xl)] p-6 mb-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-['DM_Mono'] text-sm font-semibold" style={{ color: 'var(--color-slate)' }}>{current.token_number}</span>
                      <StatusPill status={current.status} />
                      {current.is_emergency && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-['Sora'] text-[10px] font-bold" style={{ background: 'rgba(185,28,28,0.10)', color: '#B91C1C' }}>
                          EMERGENCY
                        </span>
                      )}
                    </div>
                    <h1 className="font-['Sora'] text-3xl font-bold" style={{ color: '#1C1917' }}>{current.patient_name}</h1>
                    <p className="font-['Inter'] text-sm mt-1" style={{ color: '#78716C' }}>
                      {current.patient_phone} · {current.entry_method}
                    </p>
                    {current.chief_complaint && (
                      <p className="font-['Inter'] text-sm mt-3 p-3 rounded-[10px]" style={{ background: 'rgba(61,90,128,0.04)', color: '#44403C' }}>
                        <strong>Chief complaint:</strong> {current.chief_complaint}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {current.status !== 'in-consultation' && (
                      <CopperButton size="md" onClick={handleStartConsultation} data-testid="start-consultation-btn">
                        <ChevronRight size={16} strokeWidth={1.75} />
                        Start Consultation
                      </CopperButton>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes + Prescription */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="glass-primary rounded-[var(--radius-xl)] p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={16} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
                    <h3 className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>Consultation Notes</h3>
                  </div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Examination findings, diagnosis, plan…"
                    className="w-full glass-subtle rounded-[var(--radius-md)] p-4 font-['Inter'] text-sm outline-none resize-none"
                    style={{ color: '#1C1917', minHeight: '180px' }}
                    data-testid="doctor-notes-input"
                  />
                </div>

                <div className="glass-primary rounded-[var(--radius-xl)] p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={16} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
                    <h3 className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>Prescription</h3>
                  </div>
                  <textarea
                    value={prescription}
                    onChange={(e) => setPrescription(e.target.value)}
                    placeholder="Rx: medications, dosage, duration…"
                    className="w-full glass-subtle rounded-[var(--radius-md)] p-4 font-['Inter'] text-sm outline-none resize-none"
                    style={{ color: '#1C1917', minHeight: '180px' }}
                    data-testid="doctor-prescription-input"
                  />
                </div>
              </div>

              {/* Charges */}
              <div className="glass-primary rounded-[var(--radius-xl)] p-5 mb-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Receipt size={16} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
                    <h3 className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>Apply Charges</h3>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <IndianRupee size={16} strokeWidth={2} style={{ color: '#B5612E' }} />
                    <span className="font-['DM_Mono'] text-2xl font-semibold" style={{ color: '#B5612E' }} data-testid="doctor-total-charges">
                      {totalCharges}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {DEFAULT_CHARGES.map(c => {
                    const active = !!selectedCharges[c.code];
                    return (
                      <button
                        key={c.code}
                        onClick={() => toggleCharge(c.code)}
                        className="text-left p-3 rounded-[var(--radius-md)] transition-all duration-180"
                        style={{
                          background: active ? 'rgba(212, 120, 74, 0.10)' : 'rgba(255,255,255,0.5)',
                          border: active ? '1px solid rgba(212, 120, 74, 0.45)' : '1px solid rgba(28,25,23,0.06)'
                        }}
                        data-testid={`charge-${c.code}`}
                      >
                        <div className="font-['Sora'] text-sm font-semibold" style={{ color: active ? '#B5612E' : '#1C1917' }}>{c.label}</div>
                        <div className="font-['DM_Mono'] text-xs mt-0.5" style={{ color: '#78716C' }}>₹{c.amount}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2">
                  <label className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>Custom amount (₹):</label>
                  <input
                    type="number"
                    min="0"
                    value={customCharge}
                    onChange={(e) => setCustomCharge(e.target.value)}
                    placeholder="0"
                    className="glass-subtle rounded-[var(--radius-md)] h-9 px-3 font-['DM_Mono'] text-sm outline-none w-32"
                    style={{ color: '#1C1917' }}
                    data-testid="doctor-custom-charge-input"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 justify-end">
                <button onClick={handleSaveNotes} className="btn-glass-3d h-[46px] px-5 rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold" data-testid="save-notes-btn">
                  Save notes
                </button>
                <button onClick={handleSendToBilling} className="btn-copper-3d h-[52px] px-6 rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center gap-2" data-testid="send-to-billing-btn">
                  <Receipt size={16} strokeWidth={2} />
                  Apply &amp; Send to Billing
                </button>
                <button onClick={handleComplete} className="btn-success-3d h-[52px] px-6 rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center gap-2" data-testid="complete-no-charge-btn">
                  <CheckCircle2 size={16} strokeWidth={2} />
                  Complete (no charge)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </RoleShell>
  );
};

export default DoctorView;
